import React from 'react';
import { Target, Heart, Eye, Users, Plane, Building, MapPin, Clock } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Integrity',
      description: 'We operate with honesty, professionalism, and the highest ethical standards in every interaction.',
      color: 'bg-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    },
    {
      icon: Clock,
      title: 'Reliability',
      description: 'Punctual, dependable transportation is the foundation of our service. We focus on consistency and precision in every trip.',
      color: 'bg-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      icon: Eye,
      title: 'Transparency',
      description: 'Clear communication and open processes ensure our clients always know what to expect.',
      color: 'bg-sky-600',
      bgColor: 'bg-sky-50',
      borderColor: 'border-sky-200'
    },
    {
      icon: Users,
      title: 'Accountability',
      description: 'We take responsibility for our commitments and follow through on every service we provide.',
      color: 'bg-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      icon: Target,
      title: 'Client-First Mindset',
      description: 'Our clients\' success is our priority. We work closely with each partner to deliver reliable transportation solutions tailored to their needs.',
      color: 'bg-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    }
  ];


  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Mission Section */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-navy-900">About </span>
            <span className="text-navy-900">In</span>
            <span className="text-primary-600">Timelog</span>
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-2 border-primary-100">
              <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-6">Our Mission</h3>
              <p className="text-xl md:text-2xl text-primary-600 font-medium leading-relaxed mb-6">
                Delivering reliability, precision, and trust—keeping crews and clients moving, right on time.
              </p>
              <p className="text-lg text-navy-600 leading-relaxed">
                We provide professional transportation services through experienced drivers, dedicated fleet vehicles, and coordinated operations that prioritize safety, efficiency, and on-time performance.
              </p>
            </div>
          </div>
        </div>

        {/* Technology Platform Section */}
        <div className="mb-20">
          <div className="bg-gradient-to-br from-primary-600 to-navy-900 rounded-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Purpose-Built Technology Platform</h3>
              <p className="text-lg text-primary-100 max-w-3xl mx-auto">
                InTimelog leverages a purpose-built transportation technology platform designed to support seamless operations and high service reliability.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Driver Operations */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-primary-600" />
                </div>
                <h4 className="text-xl font-bold mb-3">Driver Operations</h4>
                <p className="text-primary-100 mb-4">
                  Real-time trip management tools keep drivers connected, informed, and operating efficiently throughout every shift.
                </p>
                <ul className="space-y-2 text-sm text-primary-100">
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Live trip updates</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Flight tracking integration</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>GPS navigation</span>
                  </li>
                </ul>
              </div>

              {/* Crew Experience */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4">
                  <Plane className="w-6 h-6 text-primary-600" />
                </div>
                <h4 className="text-xl font-bold mb-3">Crew Experience</h4>
                <p className="text-primary-100 mb-4">
                  Passengers, airline crews, and clients stay informed with real-time visibility, driver information, and direct communication for a smooth and reliable transportation experience.
                </p>
                <ul className="space-y-2 text-sm text-primary-100">
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Real-time vehicle tracking</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Driver profiles & identification</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Two-way communication</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Service feedback</span>
                  </li>
                </ul>
              </div>

              {/* Command Center */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-primary-600" />
                </div>
                <h4 className="text-xl font-bold mb-3">Command Center</h4>
                <p className="text-primary-100 mb-4">
                  A centralized dispatch system provides real-time oversight across all trips, ensuring precision, accountability, and operational control.
                </p>
                <ul className="space-y-2 text-sm text-primary-100">
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Live fleet monitoring</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Flight data integration</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Performance tracking</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-300 rounded-full"></div>
                    <span>Safety monitoring & alerts</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="text-primary-100 italic text-lg">
                Complete control. Complete visibility. Complete peace of mind.
              </p>
              {/* Download Our App section - COMMENTED OUT - Links lead nowhere for now */}
              {/* <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-2xl mx-auto">
                <h4 className="text-xl font-bold text-white mb-2">Download Our App</h4>
                <p className="text-primary-100 mb-6">Real-time trip tracking and coordination for airline crews, hotel guests, and corporate clients.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="#"
                    className="inline-flex items-center justify-center px-5 py-3 bg-black rounded-xl hover:bg-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <svg className="w-7 h-7 mr-3" viewBox="0 0 24 24" fill="white">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-xs text-gray-300">Download on the</div>
                      <div className="text-base font-semibold text-white">App Store</div>
                    </div>
                  </a>
                  <a
                    href="#"
                    className="inline-flex items-center justify-center px-5 py-3 bg-black rounded-xl hover:bg-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <svg className="w-7 h-7 mr-3" viewBox="0 0 24 24" fill="white">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-xs text-gray-300">Get it on</div>
                      <div className="text-base font-semibold text-white">Google Play</div>
                    </div>
                  </a>
                </div>
              </div> */}
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4">Our Core Values</h3>
            <p className="text-lg text-navy-600 max-w-2xl mx-auto">
              These principles guide every decision we make and every service we provide.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={index}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 hover:transform hover:scale-105 shadow-lg ${value.bgColor} ${value.borderColor}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${value.color}`}>
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <h4 className="text-base font-semibold text-navy-900 mb-2">{value.title}</h4>
                  <p className="text-navy-600 text-xs leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Clients Section */}
        <div>
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4">Who We Serve</h3>
            <p className="text-lg text-navy-600 max-w-3xl mx-auto">
              Specialized transportation services built for airline operations and extended to hotels and corporate clients.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Airlines */}
            <div className="bg-gradient-to-br from-primary-50 to-navy-50 rounded-2xl p-8 border-2 border-primary-200">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <Plane className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-navy-900 mb-3">Airlines</h4>
                <p className="text-base text-navy-700 mb-4">
                  InTimelog provides reliable transportation solutions tailored to the needs of airline crews and operational teams.
                </p>
                <p className="text-sm text-navy-600 font-medium mb-4">Services include:</p>
              </div>

              <div className="space-y-3">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Crew Transportation</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Training Center Transfers</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Hotel Transfers</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Airport Shuttle Services</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hotels & Hospitality */}
            <div className="bg-gradient-to-br from-primary-50 to-navy-50 rounded-2xl p-8 border-2 border-primary-200">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <Building className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-navy-900 mb-3">Hotels & Hospitality</h4>
                <p className="text-base text-navy-700 mb-4">
                  We support hotels with dependable transportation for guests and airline crews, ensuring seamless coordination between properties and airports.
                </p>
                <p className="text-sm text-navy-600 font-medium mb-4">Services include:</p>
              </div>

              <div className="space-y-3">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Guest Transportation</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Crew Hotel Transfers</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Airport Pickups & Drop-offs</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Scheduled Shuttle Services</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Corporate Clients */}
            <div className="bg-gradient-to-br from-primary-50 to-navy-50 rounded-2xl p-8 border-2 border-primary-200">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-navy-900 mb-3">Corporate Clients</h4>
                <p className="text-base text-navy-700 mb-4">
                  InTimelog delivers professional transportation solutions for business travel and operational needs across the Dallas–Fort Worth area.
                </p>
                <p className="text-sm text-navy-600 font-medium mb-4">Services include:</p>
              </div>

              <div className="space-y-3">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Executive Transportation</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Airport Transfers</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Scheduled Corporate Shuttles</span>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
                    <span className="text-navy-700 text-sm font-medium">Event & Group Transportation</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;